import React, { Fragment, useEffect, useRef, useState, useCallback } from "react";
import styled from "styled-components";
import { EventQueryEnum } from "../models/enums/EventQueryEnum";
import PageLoader from "../components/PageLoader";
import { PaddedPage } from "../components/PageLayout";
import { Colors, Icon, Icons, Loader, LoaderSizes } from '@sellout/ui';
import useListOrders from '../hooks/useListOrders.hook';
import UserOrderCard from "../../src/components/User/UserOrderCard";
import * as Time from '@sellout/utils/.dist/time';
import Masonry from "react-masonry-component";
import { useDispatch, useSelector } from "react-redux";
import { BackstageState } from "../redux/store";
import {gql} from '@apollo/client';
import { useLazyQuery,useQuery } from '@apollo/react-hooks';
import SHARED_TICKET_BY_EMAIL_PHONENUMBER from "@sellout/models/.dist/graphql/queries/getSharedTicketsByEmailPhoneNumber.query";
import { ModalTypes } from "../components/modal/Modal";
import * as OrderActions from "../redux/actions/order.actions";
import * as AppActions from "../redux/actions/app.actions";
import GET_PROFILE from "@sellout/models/.dist/graphql/queries/profile.query";


type UserDashboardProps = {};
const SubNavContainer = styled.div`
  display: flex;
  margin-bottom: 24px;
`;

type SubNavItemProps = {
    active: boolean;
};

const SubNavItem = styled.div<SubNavItemProps>`
  transition: all 0.2s;
  color: ${(props) => (props.active ? `${Colors.Grey1}` : `${Colors.Grey4}`)};
  font-weight: 600;
  font-size: 1.8rem;
  cursor: pointer;
  margin-right: 24px;
`;

const NoContentContainer = styled.div`
    width: 100%;
    height: calc(100% - 60px);
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: ${Colors.Grey6};
    border-radius: 16px;
`;

const Text = styled.div`
    font-weight: 600;
    font-size: 1.5rem;
    line-height: 2rem;
    color: #828282;
    margin: 15px 0;
    text-align: center;
`;

const Button = styled.button`
    background: #FF6802;
    color: #FFFFFF;
    width: 200px;
    font-size: 1.4rem;
    line-height: 1.4rem;
    padding: 0 25px;
    border-radius: 5px;
    height: 40px;
    border:none;
    font-weight:600;
    cursor:pointer;
`;

export const Flex = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-wrap:wrap;
  height:100%;
`;

const ScrollContainer = styled.div`
  height: 100%;
  overflow-y: auto;
`;

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px;
`;

const USER = gql`
    query user {
        user {
            _id
            }
    }
`;

const UserDashboard: React.FC<UserDashboardProps> = () => {
    const { data:profileData } = useQuery(GET_PROFILE);
    const orderState = useSelector((state: BackstageState) => state.order);
    const { sharedLoginEmail,sharedLoginPhoneNumber,confirmAcceptModal } = orderState;
  const dispatch = useDispatch();

    const isMyTicketUrl = window.location.pathname
    const [queryKey, setQueryKey] = React.useState(
        EventQueryEnum.MainEventListUpComing
    );

    const { data } = useQuery(USER)

    const scrollContainer = useRef<HTMLDivElement>(null);
    const [hasMoreOrders, setHasMoreOrders] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);

    const { orders, fetchMore, loading: ordersLoading,refetch } = useListOrders({
        variables: {
            query: { userIds: [data?.user?._id],isMyTicketScreen:true},
            pagination: {
                pageSize: 20,
                pageNumber: 1,
            },
        },
        context: {
            debounceKey: 'QUERY_ORDERS',
            debounceTimeout: 250,
        },
    });
        useEffect(() => {
            if (isMyTicketUrl ==="/my-tickets"){
                refetch()
            }
        }, [refetch,fetchMore,isMyTicketUrl])

    const handleScroll = useCallback(() => {
        if (!scrollContainer.current || !fetchMore || !hasMoreOrders || isFetchingMore) return;

        const { scrollTop, scrollHeight, clientHeight } = scrollContainer.current;
        if (scrollTop + clientHeight >= scrollHeight - 500) {
            setIsFetchingMore(true);
            fetchMore({
                variables: {
                    pagination: {
                        pageSize: 10,
                        pageNumber: Math.ceil((orders?.length || 0) / 10) + 1,
                    },
                },
                updateQuery: (prev, { fetchMoreResult }) => {
                    setIsFetchingMore(false);

                    if (!fetchMoreResult || !fetchMoreResult?.orders || fetchMoreResult?.orders?.length === 0) {
                        setHasMoreOrders(false);
                        return prev || { orders: [] };
                    }

                    const existingOrderIds = new Set((prev?.orders ?? []).map((order: any) => order._id));
                    const newOrders = fetchMoreResult.orders.filter((order) => !existingOrderIds.has(order._id));

                    return {
                        ...prev,
                        orders: [...(prev?.orders ?? []), ...newOrders],
                    };
                },
            });
        }
    }, [orders?.length, fetchMore, hasMoreOrders, isFetchingMore]);

    useEffect(() => {
        const container = scrollContainer.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll, queryKey]);

    useEffect(() => {
        setHasMoreOrders(true);
        setIsFetchingMore(false);
    }, [queryKey]);

        const [acceptModalDetails] = useLazyQuery(SHARED_TICKET_BY_EMAIL_PHONENUMBER, {
            fetchPolicy: "network-only",
            onCompleted(data) {
              let tickets = data?.getSharedTicketsByEmailPhoneNumber?.sharingData?.tickets
              let upgrades = data?.getSharedTicketsByEmailPhoneNumber?.sharingData?.upgrades
              const openAcceptModal =tickets?.length > 0 || upgrades?.length > 0
              if (openAcceptModal) {
                dispatch(OrderActions.SetSharedAcceptModal(data?.getSharedTicketsByEmailPhoneNumber?.sharingData
                ));
                if (!confirmAcceptModal){
                  dispatch(AppActions.pushModal(ModalTypes.AcceptShareTicket));
                }
                dispatch(OrderActions.SetConfirmAcceptModal(false))
                }
            },
          });
          React.useEffect(() => { 
            if ((!confirmAcceptModal) ){
              acceptModalDetails({ variables: { email:sharedLoginEmail ||profileData?.user?.email, phoneNumber:sharedLoginPhoneNumber ||profileData?.user?.phoneNumber
              }});
            }
            }, [sharedLoginEmail, sharedLoginPhoneNumber, acceptModalDetails,profileData?.user?.email,profileData?.user?.phoneNumber]);


    const orderList = orders && orders.filter((order: any) => {
        // if (!order.event) return false;
        if (EventQueryEnum.MainEventListUpComing === queryKey) {
            if ((order?.event?.schedule.startsAt + Time.HOUR * 3) > Time.now()) {
                return true;
            }
        } else if ((order?.event?.schedule.startsAt + Time.HOUR * 3) < Time.now()) {
            return true;
        }
        return false;
    });

    const text = EventQueryEnum.MainEventListUpComing === queryKey
        ? 'You don\'t have any upcoming orders. Purchase a ticket to populate your dashboard.'
        : 'You don\'t have any past orders. Purchase a ticket to populate your dashboard.';

    return (
        <ScrollContainer ref={scrollContainer}>
            <Fragment>
                <PageLoader nav={true} fade={Boolean(!ordersLoading)} />
                <PaddedPage>
                <SubNavContainer>
                    <SubNavItem
                        onClick={() => {
                            setQueryKey(EventQueryEnum.MainEventListUpComing)
                        }}
                        active={EventQueryEnum.MainEventListUpComing === queryKey}
                    >
                        Upcoming
                    </SubNavItem>
                    <SubNavItem
                        onClick={() => {
                            setQueryKey(EventQueryEnum.MainEventListPast)
                        }}
                        active={EventQueryEnum.MainEventListPast === queryKey}
                    >
                        Past
                    </SubNavItem>
                </SubNavContainer>
                {!ordersLoading && orderList && orderList?.length > 0 ? <Masonry options={{ horizontalOrder: true }} enableResizableChildren>
                    {EventQueryEnum.MainEventListUpComing === queryKey &&
                        orderList.map((order, index) => {
                            return <UserOrderCard key={index} order={order} />
                        })}

                    {EventQueryEnum.MainEventListPast === queryKey &&
                        orderList.map((order, index) => {
                            return <UserOrderCard key={index} order={order} />
                        })}

                </Masonry> : <>{!ordersLoading &&
                    <NoContentContainer>
                        <Icon
                            color={Colors.Grey5}
                            size={80}
                            icon={Icons.TicketSolid}
                        />
                        <Text>{text}</Text>
                        <Button
                            onClick={() => {
                                window.open('https://events.sellout.io', '_blank');
                            }}
                        >
                            BROWSE EVENTS
                        </Button>
                    </NoContentContainer>} </>}
                {isFetchingMore && (
                    <LoaderContainer>
                        <Loader size={LoaderSizes.Large} color={Colors.Orange} />
                    </LoaderContainer>
                )}
                </PaddedPage>
            </Fragment>
        </ScrollContainer>
    )
};

export default UserDashboard;
